import { Link } from "react-router-dom";
import { Mail, MapPin, Linkedin } from "lucide-react";

import logo from "@/assets/logo.png";

const footerLinks = {
  company: [
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Case Studies", path: "/case-studies" },
    { name: "Contact", path: "/contact" },
  ],
  services: [
    { name: "AI Services", path: "/ai-services" },
    { name: "AI Implementation", path: "/ai-implementation" },
    { name: "AI & Machine Learning", path: "/services" },
    { name: "Full-Stack Engineering", path: "/services" },
    { name: "Analytics & Data", path: "/services" },
    { name: "Cloud & DevOps", path: "/services" },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-[#0f172a] text-white" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img
                src={logo}
                alt="VANCO"
                className="h-10 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Empowering enterprises to scale smarter, faster with AI that drives meaningful impact.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.linkedin.com/company/vanco-ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#243b7f] hover:text-white transition-all duration-300"
                data-testid="footer-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://x.com/vancoai"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#000000] hover:text-white transition-all duration-300"
                data-testid="footer-x"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6 font-['Manrope']">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-[#f7c550] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6 font-['Manrope']">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={link.name + index}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-[#f7c550] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6 font-['Manrope']">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-[#f7c550] mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:info@vanco.ai"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-email"
                >
                  info@vanco.ai
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Registered Entities */}
        <div className="py-8 border-t border-slate-800" data-testid="footer-entities">
          <h4 className="text-slate-300 text-xs font-semibold uppercase tracking-widest mb-6">
            Registered Entities
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start gap-3" data-testid="footer-entity-usa">
              <MapPin size={18} className="text-[#f7c550] mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-white font-medium">KIDLIN Labs Inc.</p>
                <p className="text-slate-500 text-xs mb-1">United States</p>
                <p className="text-slate-400">2604 Whittier Place, Wilmington, DE 19808, USA</p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="footer-entity-india">
              <MapPin size={18} className="text-[#f7c550] mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-white font-medium">CODEBUGGED Organization Pvt Ltd</p>
                <p className="text-slate-500 text-xs mb-1">India</p>
                <a
                  href="https://maps.app.goo.gl/1p26zC9Hvp7j4qpYA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-location"
                >
                  4th Floor, Block A, BHIVE Premium HSR Campus, AKR Tech Park, 7th Mile, Hosur Road, Bengaluru, Karnataka 560068, India
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} VANCO. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center md:justify-end">
            <Link to="/privacy-policy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Terms of Use
            </Link>
            <Link to="/privacy-cookies" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Privacy & Cookies
            </Link>
            <Link to="/human-rights" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Human Rights
            </Link>
            <Link to="/modern-slavery" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Modern Slavery
            </Link>
            <Link to="/ohs-policy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              OH&S Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
